import { FormFieldConfigModel } from '@pxl/models/common';
import { PxlValidators } from './validators';

export class FormFieldConfigs {
    public static getCommonAddressConstraints(): FormFieldConfigModel {
        return {
            streetHouseNumber: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            postalCode: {
                isRequired: true,
                validators: [PxlValidators.required(), PxlValidators.maxLength(20)],
            },
            city: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            country: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
        };
    }

    public static getCommonPatientConstraints(): FormFieldConfigModel[] {
        const formFieldConfigAnimal: FormFieldConfigModel = {
            name: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            nickname: {
                isRequired: false,
                validators: [],
            },
            species: {
                isRequired: false,
                validators: [],
            },
            animalGender: {
                isRequired: false,
                validators: [],
            },
            animalDateOfBirth: {
                isRequired: false,
                validators: [PxlValidators.date()],
            },
            breed: {
                isRequired: false,
                validators: [],
            },
            animalChipNo: {
                isRequired: false,
                validators: [PxlValidators.maxLength(25), PxlValidators.numbersOnly('petsxl.ui.shared.text.validator.only-numbers')],
            },
            equinePassportNumber: {
                isRequired: false,
                validators: [],
            },
        };

        const formFieldConfigLivestock: FormFieldConfigModel = {
            ...formFieldConfigAnimal,
            animalGender: {
                isRequired: false,
                validators: [],
            },
            animalDateOfBirth: {
                isRequired: false,
                validators: [],
            },
        };

        return [formFieldConfigAnimal, formFieldConfigLivestock];
    }

    public static readonly CommonPersonalDataConstraints = {
        salutation: {
            isRequired: true,
            validators: [PxlValidators.required()],
        },
        firstName: {
            isRequired: true,
            validators: [PxlValidators.required()],
        },
        lastName: {
            isRequired: true,
            validators: [PxlValidators.required()],
        },
        culture: {
            isRequired: true,
            validators: [PxlValidators.required()],
        },
        phoneNumber: {
            isRequired: false,
            requiredOr: ['mobileNumber'],
            validators: [PxlValidators.requiredOr(['mobileNumber'], 'petsxl.ui.shared.text.validator.phone-or-mobile-required')],
        },
        mobileNumber: {
            isRequired: false,
            requiredOr: ['phoneNumber'],
            validators: [PxlValidators.requiredOr(['phoneNumber'], 'petsxl.ui.shared.text.validator.phone-or-mobile-required')],
        },
        website: {
            isRequired: false,
            validators: [PxlValidators.www()],
        },
        dateOfBirthInput: {
            isRequired: false,
            validators: [PxlValidators.date()],
        },
    };

    public static getCommonPersonalDataConstraints(): FormFieldConfigModel {
        return Object.assign({}, FormFieldConfigs.CommonPersonalDataConstraints);
    }

    public static getAppointmentRegistrationPersonalDataConstraints(): FormFieldConfigModel {
        return Object.assign({}, FormFieldConfigs.CommonPersonalDataConstraints, {});
    }

    public static getCommonPatientMedicalDataConstraints(): FormFieldConfigModel {
        return {
            bloodGroup: {
                isRequired: false,
                validators: [],
            },
        };
    }

    public static getCommonPatientIntoleranceOrAllergyConstraints(): FormFieldConfigModel {
        return {
            intoleranceOrAllergy: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            notes: {
                isRequired: false,
                validators: [PxlValidators.maxLength(1024)],
            },
        };
    }

    public static getCommonPatientPreExistingConditionConstraints(): FormFieldConfigModel {
        return {
            preExistingCondition: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            notes: {
                isRequired: false,
                validators: [PxlValidators.maxLength(1024)],
            },
        };
    }

    public static getCommonPatientMedicationInformationConstraints(): FormFieldConfigModel {
        return {
            medication: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            notes: {
                isRequired: false,
                validators: [PxlValidators.maxLength(1024)],
            },
        };
    }

    public static getCommonPatientSurgeryConstraints(): FormFieldConfigModel {
        return {
            surgery: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            date: {
                isRequired: true,
                validators: [PxlValidators.required(), PxlValidators.date()],
            },
            notes: {
                isRequired: false,
                validators: [PxlValidators.maxLength(1024)],
            },
        };
    }

    public static getCommonPatientBloodTransfusionConstraints(): FormFieldConfigModel {
        return {
            bloodGroup: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            species: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            date: {
                isRequired: true,
                validators: [PxlValidators.required(), PxlValidators.date()],
            },
            notes: {
                isRequired: false,
                validators: [PxlValidators.maxLength(1024)],
            },
        };
    }

    public static getCommonPatientNonDomesticTravelConstraints(): FormFieldConfigModel {
        return {
            country: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            startDate: {
                isRequired: false,
                validators: [PxlValidators.date()],
            },
            endDate: {
                isRequired: false,
                validators: [PxlValidators.date()],
            },
            notes: {
                isRequired: false,
                validators: [PxlValidators.maxLength(1024)],
            },
        };
    }

    public static getCommonPatientNoteConstraints(): FormFieldConfigModel {
        return {
            text: {
                isRequired: true,
                validators: [PxlValidators.required(), PxlValidators.maxLength(1024)],
            },
        };
    }

    public static getSelfCheckInPersonalDataConstraints(): FormFieldConfigModel {
        return Object.assign({}, FormFieldConfigs.CommonPersonalDataConstraints, {});
    }

    public static getStrictPatientConstraints(): FormFieldConfigModel[] {
        const formFieldConfigAnimal: FormFieldConfigModel = {
            name: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            species: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            animalGender: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            animalDateOfBirth: {
                isRequired: true,
                validators: [PxlValidators.required(), PxlValidators.date()],
            },
            breed: {
                isRequired: true,
                validators: [PxlValidators.required()],
            },
            animalChipNo: {
                isRequired: false,
                validators: [PxlValidators.maxLength(25), PxlValidators.numbersOnly('petsxl.ui.shared.text.validator.only-numbers')],
            },
        };
        const formFieldConfigLivestock: FormFieldConfigModel = {
            ...formFieldConfigAnimal,
            animalGender: {
                isRequired: false,
                validators: [],
            },
            animalDateOfBirth: {
                isRequired: false,
                validators: [],
            },
        };

        return [formFieldConfigAnimal, formFieldConfigLivestock];
    }
}
